import { useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import { DisplayQuoteToggleGroup } from '@/settings/DisplayQuoteToggleGroup';
import { useCoins } from '@/shared/api';
import { Button } from '@/shared/Button';
import { Coin } from '@/shared/Coin';
import { Digits } from '@/shared/Digits';
import { cn } from '@/shared/react';
import { Table } from '@/shared/Table';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { Widget } from '@/shared/Widget';
import { Gate } from '../../gate';

export const MarketTable: FC<{
  className?: string;
}> = ({ className }) => {
  const { t } = useLingui();
  const coins = useCoins();
  const [settings] = useSettings();
  usePupeteerSignal(!!coins.data);

  return (
    <Widget className={cn('flex flex-col', className)}>
      <div className="flex gap-2 justify-between shrink-0">
        <h3 className="text-base mb-4 font-normal">{t`Market`}</h3>
        <DisplayQuoteToggleGroup size={6} variant="button" />
      </div>
      <div className="grow shrink scrollbar-none overflow-auto">
        <Table
          rows={coins.data?.filter((x) => x.on_market) ?? []}
          loading={coins.isLoading}
          rowKey={(r) => r.slug}
          className="[&_tr]:transition-colors"
          columns={[
            {
              key: 'index',
              title: t`#`,
              width: 32,
              mode: 'table',
              className: 'text-content-tertiary',
              render: (_, i) => i + 1,
            },
            {
              key: 'asset',
              title: t`Coin`,
              render: (r) => (
                <Link
                  to={`/${settings.language}/buy-and-sell/${r.slug}`}
                  workspace="public"
                >
                  <Coin size={6} coin={r} />
                </Link>
              ),
            },
            {
              key: 'price',
              title: t`Last Price`,
              render: (r) => (
                <Digits
                  value={r[`price_${settings.displayQuote}`]}
                  coin={settings.displayQuote}
                />
              ),
            },
            {
              key: 'volume',
              title: t`Volume (24H)`,
              render: (r) => (
                <Digits
                  value={r[`volume_${settings.displayQuote}`]}
                  coin={settings.displayQuote}
                />
              ),
            },
            {
              key: 'change',
              title: t`Change (24H)`,
              render: (r) => (
                <Digits
                  value={r.price_change_percent}
                  unit="%"
                  sign="always"
                  color="auto"
                />
              ),
            },
            {
              key: 'actions',
              title: t`Actions`,
              width: 150,
              className: '!px-0',
              render: (r) => (
                <Gate
                  hooks={[
                    Gate.hooks.hasFeature('trade'),
                    Gate.hooks.isStandalone(false),
                  ]}
                >
                  <div className="flex flex-row gap-2">
                    <Link
                      to={`/trade/instant?coin=${r.slug}`}
                      className="contents"
                    >
                      <Button
                        size={8}
                        variant="outline"
                        className="desktop:w-[72px] w-[100px]"
                        type="button"
                      >
                        {t`Buy / Sell`}
                      </Button>
                    </Link>

                    <Link
                      to={`/${settings.language}/buy-and-sell/${r.slug}`}
                      workspace="public"
                      className="contents"
                    >
                      <Button
                        size={8}
                        variant="outline"
                        className="desktop:w-[72px] w-[100px]"
                        type="button"
                      >
                        {t`Details`}
                      </Button>
                    </Link>
                  </div>
                </Gate>
              ),
            },
          ]}
        />
      </div>
    </Widget>
  );
};
